/**
 * Die Regeln der KA-Eingabemasken: welche Maske welche Felder zeigt, wie ein Feld
 * heisst, welches Feld Pflicht ist und wie ein Wert im Punkt gelesen und
 * geschrieben wird.
 *
 * Alles kommt aus dem Feldkatalog (`ka-felder.ts`). Die Masken zaehlen deshalb
 * kein Feld selbst auf; ein neues Feld im Katalog erscheint von allein, und zwar
 * in der Reihenfolge des amtlichen Bogens - erst beschreiben, dann das
 * Horizontsymbol (Feld 58 steht dort am Ende, ebenso die Substratart, Feld 59).
 *
 * Ohne Angular, damit sich die Regeln ohne Fenster pruefen lassen.
 *
 * **Diese Datei enthaelt keine Schluessellisten** (siehe `ka-listen.ts` zur
 * Rechtefrage). Solange die Rechte ungeklaert sind, sind alle Listenfelder freie
 * Eingaben.
 */

import { GEMEINSAME_PFADE, KA6_FELDER, KA6_KURZAUFNAHME_VORLAEUFIG, KaBlock, KaFeld, kaFeld } from './ka-felder';
import { KaEintrag } from './ka-modell';
import { leseKaWert } from './ka-anzeige';

/** Die vier Masken, je eine je Block des Bogens. */
export type KaMaskeId = 'titel' | 'situation' | 'horizonte' | 'profil';

export interface KaMaske {
	id: KaMaskeId;
	/**
	 * Letztes Stueck der Adresse (`bohrpunkte/<id>/<route>`) UND der Name in der
	 * `bpHl`-Anweisung der Lese-Uebersicht: Beides muss dasselbe Wort sein, sonst
	 * fuehrt der Klick ins Leere.
	 */
	route: string;
	/** Beschriftung im Bearbeiten-Fenster, wie der Block im Bogen heisst. */
	titel: string;
	/** Bloecke des Katalogs, die diese Maske bearbeitet. */
	bloecke: readonly KaBlock[];
}

export const KA_MASKEN: readonly KaMaske[] = [
	{ id: 'titel', route: 'KA-Titeldaten', titel: 'Titeldaten', bloecke: ['titel'] },
	{ id: 'situation', route: 'KA-Aufnahmesituation', titel: 'Aufnahmesituation', bloecke: ['situation'] },
	// Horizontbezogene Daten I und II sind im Bogen zwei Tabellen ueber denselben
	// Horizonten. In der App ist es EINE Tabelle (siehe `ka-modell.ts`), also EINE Maske.
	{ id: 'horizonte', route: 'KA-Horizonte', titel: 'Horizonte', bloecke: ['horizont1', 'horizont2'] },
	{ id: 'profil', route: 'KA-Profilkennzeichnung', titel: 'Profilkennzeichnung', bloecke: ['profil'] },
];

export function kaMaske(id: KaMaskeId): KaMaske {
	const m = KA_MASKEN.find(k => k.id === id);
	if (!m) throw new Error(`Unbekannte KA-Maske: ${id}`);
	return m;
}

/** Die Maske, in der ein Block bearbeitet wird - fuer die `bpHl`-Anweisung der Uebersicht. */
export function kaMaskeFuerBlock(block: KaBlock): KaMaske {
	const m = KA_MASKEN.find(k => k.bloecke.includes(block));
	if (!m) throw new Error(`Kein KA-Maske fuer Block: ${block}`);
	return m;
}

/**
 * Ein Feld des Katalogs, das es geben MUSS. Fehlt es, ist der Katalog falsch und
 * nicht die Maske: Eine Maske, die auf eine Kennung zeigt, die es nicht gibt, soll
 * das beim ersten Testlauf sagen und nicht stumm ein leeres Feld zeichnen.
 */
export function kaFeldSicher(id: string): KaFeld {
	const f = kaFeld(id);
	if (!f) throw new Error(`Der KA-Feldkatalog kennt kein Feld "${id}".`);
	return f;
}

/**
 * Steht dieses Feld nicht im KA-Teil, sondern an seiner bisherigen Stelle im Punkt
 * (Nummer, Datum, Bearbeiter, Koordinaten, Aufnahmetiefe, Bemerkungen)?
 *
 * Solche Felder bekommen in der Maske eine eigene, von Hand geschriebene Zeile:
 * Das Datum braucht ein Datumsfeld, der Bearbeiter die Gastmodus-Regel, die
 * Koordinaten ihr Bezugssystem. Sie aus dem Katalog zu erzeugen wuerde diese
 * Eigenheiten verschlucken.
 */
export function istGemeinsamesFeld(feld: KaFeld): boolean {
	return feld.pfade.some(p => GEMEINSAME_PFADE.includes(p));
}

/** Einmal gerechnet und gemerkt, siehe {@link kaMaskenFelder}. */
const FELDER_JE_MASKE = new Map<KaMaskeId, KaFeld[]>();

/**
 * Die Felder, die eine Maske aus dem Katalog erzeugt: die Felder ihrer Bloecke
 * ohne die gemeinsamen (siehe {@link istGemeinsamesFeld}), in der Reihenfolge des
 * Bogens.
 *
 * Das Ergebnis wird gemerkt, und zwar aus zwei Gruenden. Es aendert sich nie (der
 * Katalog ist fest). Und die Vorlage fragt bei JEDEM Prueflauf von Angular danach:
 * Eine jedes Mal frisch gebaute Liste liesse Angular jedes Mal die ganze Tabelle
 * vergleichen. **Die Liste gehoert nicht dem Aufrufer** - sie wird nur gelesen,
 * nicht umsortiert.
 */
export function kaMaskenFelder(id: KaMaskeId): KaFeld[] {
	const gemerkt = FELDER_JE_MASKE.get(id);
	if (gemerkt) return gemerkt;
	const bloecke = kaMaske(id).bloecke;
	const felder = KA6_FELDER.filter(f => bloecke.includes(f.block) && !istGemeinsamesFeld(f));
	FELDER_JE_MASKE.set(id, felder);
	return felder;
}

/**
 * Pflichtfeld? Das sind die Felder der Kurzaufnahme (der "verkuerzte Datensatz":
 * die aus fachlicher Sicht mindestens zu erhebenden Merkmale).
 *
 * **Die Pflicht haengt am Feld, nicht am `option.service`.** Dort steht die Liste
 * der SEA-Pflichtfelder (Forstamt, Feinbodenform, Entwaesserungsverhalten ...);
 * sie gilt fuer jedes Feld dieses Namens, ueberall in der App. Die KA hat andere
 * Pflichtfelder, und die drei genannten duerfen bei einem KA-Punkt gerade nicht
 * Pflicht sein. Deshalb gibt jede KA-Maske die Pflicht am Eingabefeld selbst mit
 * und die SEA-Liste bleibt unberuehrt.
 */
export function istKaPflichtfeld(feld: KaFeld): boolean {
	return KA6_KURZAUFNAHME_VORLAEUFIG.includes(feld.id);
}

/** Feld 26 im Bogen: Ober- und Untergrenze stehen als EIN Feld im Katalog, in der Tabelle als zwei Spalten. */
export const KA_GRENZEN_FELD = 'grenzen';

/**
 * Die Spalten der Horizonttabelle: die Felder der Kurzaufnahme, dazu die
 * Bemerkungen je Horizont.
 *
 * **Warum hier gekuerzt wird und in den anderen Masken nicht:** Die Horizonttabelle
 * traegt je Feld eine SPALTE. Mit allen Feldern der beiden Horizont-Bloecke waeren
 * es ueber vierzig Spalten - auf keinem Bildschirm und schon gar nicht am Tablet
 * zu bedienen. Die Titeldaten, die Aufnahmesituation und die Profilkennzeichnung
 * sind untereinander stehende Zeilen; dort kostet ein Feld nur eine Zeile.
 *
 * Die Bemerkungen stehen nicht in der Kurzaufnahme, sind aber mit dabei: Was die
 * Maske noch nicht kann, muss irgendwo festgehalten werden koennen.
 *
 * Fuer die Vollaufnahme (Stufe 5 des Plans) wird hier erweitert; die Reihenfolge
 * kommt dann wie jetzt aus dem Katalog.
 */
export const KA_HORIZONT_SPALTEN: readonly KaFeld[] = kaMaskenFelder('horizonte')
	.filter(f => f.id !== KA_GRENZEN_FELD && (istKaPflichtfeld(f) || f.id === 'horizontBemerkungen'));

/**
 * Der Name, unter dem sich ein Eingabefeld bei Optionen, Validierung, Hilfe und
 * Kommentaren anmeldet.
 *
 * Das Vorwort `KA-` ist Pflicht: Ohne es traefe ein KA-Feld auf die Einstellungen
 * des gleichnamigen SEA-Feldes. "Humusgehalt" zum Beispiel hat in der SEA eine
 * Stufenliste und ein Hilfefenster - beides gilt fuer die SEA-Stufen, nicht fuer
 * die der KA.
 */
export function kaFeldName(feld: KaFeld, zeile?: number): string {
	return zeile === undefined ? `KA-${feld.id}` : `KA-${feld.id}[${zeile}]`;
}

/**
 * Mehrere Eintraege eines Feldes als EIN Text, mit Semikolon getrennt.
 *
 * Der Bogen sieht je Horizont drei Schreibzeilen vor; mehrere Eintraege je Feld
 * sind der Normalfall (Oxidations- und Reduktionsmerkmale, Grobbodenfraktionen).
 * Solange die Eingabe freier Text ist, ist die Semikolon-Schreibweise der
 * einfachste Weg, mehrere Eintraege zu erfassen, ohne den gespeicherten Aufbau
 * anzutasten - im Datensatz bleibt es eine Liste. Die Vollaufnahme bekommt dafuer
 * ein eigenes Eingabefeld (Stufe 5 des Plans).
 */
export function kaEintraegeAlsText(werte: unknown): string {
	if (!Array.isArray(werte)) return werte === undefined || werte === null ? '' : String(werte);
	return werte.map(w => (w === undefined || w === null ? '' : String(w))).filter(t => t.trim() !== '').join('; ');
}

/** Der eingegebene Text zurueck in die Liste der Eintraege. Leere Stuecke fallen weg. */
export function kaTextAlsEintraege(text: unknown): KaEintrag[] {
	if (text === undefined || text === null) return [];
	return String(text).split(';').map(t => t.trim()).filter(t => t !== '');
}

/**
 * Schreibt einen Wert an seinen Platz im Punkt. `[]` im Pfad steht fuer die
 * Horizontzeile `zeile`.
 *
 * Fehlt ein Zwischenstueck des Pfades, wird es angelegt - ein KA-Punkt, der vor
 * einem neuen Feld angelegt wurde, soll sich trotzdem ausfuellen lassen. Fehlt die
 * angesprochene Horizontzeile, wird NICHTS geschrieben: Eine Zeile entsteht ueber
 * "Zeile dazu" mit ihrer Kennung, nicht als Nebenwirkung einer Eingabe.
 */
export function schreibeKaWert(punkt: unknown, pfad: string, wert: unknown, zeile?: number): void {
	const teile = pfad.split('.');
	let ort = punkt as Record<string, unknown> | undefined;

	for (let i = 0; i < teile.length - 1; ++i) {
		if (!ort || typeof ort !== 'object') return;
		const teil = teile[i];
		const jeZeile = teil.endsWith('[]');
		const schluessel = jeZeile ? teil.slice(0, -2) : teil;

		if (jeZeile) {
			const liste = ort[schluessel];
			if (!Array.isArray(liste) || zeile === undefined) return;
			const eintrag = liste[zeile];
			if (!eintrag || typeof eintrag !== 'object') return;
			ort = eintrag as Record<string, unknown>;
			continue;
		}

		if (ort[schluessel] === undefined || ort[schluessel] === null) ort[schluessel] = {};
		const naechste = ort[schluessel];
		if (typeof naechste !== 'object') return;
		ort = naechste as Record<string, unknown>;
	}

	if (!ort || typeof ort !== 'object') return;
	ort[teile[teile.length - 1]] = wert;
}

/**
 * Der Wert eines Feldes als Text fuer die Eingabe. Mehrwertige Felder kommen als
 * Semikolon-Text, alles andere unveraendert.
 */
export function kaFeldText(punkt: unknown, feld: KaFeld, zeile?: number): string {
	const wert = leseKaWert(punkt, feld.pfade[0], zeile);
	if (feld.mehrfach) return kaEintraegeAlsText(wert);
	return wert === undefined || wert === null ? '' : String(wert);
}

/**
 * Der Wert eines Zahlenfeldes fuer die Eingabe.
 *
 * Leer ist `undefined`, nicht der leere Text: Das Zahlenfeld der Bibliothek
 * rechnet jeden Wert, der nicht `undefined` oder `null` ist, mit `parseFloat`
 * um - aus dem leeren Text wuerde `NaN`, und das stuende dann im Datensatz.
 */
export function kaFeldZahl(punkt: unknown, feld: KaFeld, zeile?: number): number | undefined {
	return alsZahl(leseKaWert(punkt, feld.pfade[0], zeile));
}

/** Derselbe Weg fuer einen einzelnen Pfad (Ober- und Untergrenze teilen sich ein Katalog-Feld). */
export function kaPfadZahl(punkt: unknown, pfad: string, zeile?: number): number | undefined {
	return alsZahl(leseKaWert(punkt, pfad, zeile));
}

function alsZahl(wert: unknown): number | undefined {
	if (wert === undefined || wert === null || wert === '') return undefined;
	const n = typeof wert === 'number' ? wert : parseFloat(String(wert));
	return Number.isFinite(n) ? n : undefined;
}

/**
 * Nimmt den Wert aus dem Eingabefeld entgegen und legt ihn an seinen Platz.
 *
 * `undefined`, `null` und `NaN` werden zum leeren Text: Der Datensatz kennt drei
 * Zustaende (leer, `nv`, `nb`), und "leer" ist der leere Text. `NaN` kommt aus dem
 * Zahlenfeld, wenn jemand seine Eingabe wieder loescht; als Zahl gespeichert wuerde
 * daraus beim Abgleich `null`.
 */
export function schreibeKaFeld(punkt: unknown, feld: KaFeld, wert: unknown, zeile?: number): void {
	if (feld.mehrfach) {
		schreibeKaWert(punkt, feld.pfade[0], kaTextAlsEintraege(wert), zeile);
		return;
	}
	schreibeKaWert(punkt, feld.pfade[0], leerOderWert(wert), zeile);
}

/** Derselbe Weg fuer einen einzelnen Pfad (Ober- und Untergrenze). */
export function schreibeKaPfad(punkt: unknown, pfad: string, wert: unknown, zeile?: number): void {
	schreibeKaWert(punkt, pfad, leerOderWert(wert), zeile);
}

function leerOderWert(wert: unknown): KaEintrag {
	if (wert === undefined || wert === null) return '';
	if (typeof wert === 'number') return Number.isFinite(wert) ? wert : '';
	return String(wert);
}
